import { authenticatedRequest } from '@/lib/authenticatedRequest'
import { getApiUrl } from '@/lib/serverConfig'
import type { WorkflowRunEnginePin } from '@/types/shared'

/** An executor the backend accepts for an Action; ids are chat model ids. */
export interface ActionAgentOption {
  id: string
  label: string
}

export interface WorkflowConfigResponse {
  enabled: boolean
  /** Workflow Beta (one autonomous session per run) can drive runs on this deployment; only its kill switch says no. */
  betaEngine?: boolean
  /**
   * The engine a run gets when the request names none and the workflow is not
   * pinned. Absent on an older backend, which defaulted to the harness.
   */
  defaultEngine?: 'fable' | 'harness'
  /** Present when enabled: the agents THIS backend deployment accepts. */
  agents?: ActionAgentOption[]
  /**
   * Saved workflows of this project's organization an operator pinned to an
   * engine, by saved workflow id. A placed copy linked to one always runs on
   * that engine — the backend overrides the request — so the panel says so.
   */
  enginePins?: Record<string, WorkflowRunEnginePin>
}

const CONFIG_TTL_MS = 60_000

let cached: { at: number; projectId: string | null; promise: Promise<WorkflowConfigResponse> } | null = null

async function requestWorkflowConfig(projectId: string | null): Promise<WorkflowConfigResponse> {
  // Access is per project: only projects owned by an allowlisted organization
  // may run workflows, so the answer for one project says nothing about another.
  const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''
  const response = await authenticatedRequest(`${getApiUrl()}/workflows/config${query}`, {})

  if (!response.ok) {
    if (response.status >= 500) throw new Error(`Workflow config request failed (${response.status})`)
    return { enabled: false }
  }

  return (await response.json()) as WorkflowConfigResponse
}

export function fetchWorkflowConfig(projectId: string | null = null): Promise<WorkflowConfigResponse> {
  if (cached && cached.projectId === projectId && Date.now() - cached.at < CONFIG_TTL_MS) return cached.promise

  const entry = {
    at: Date.now(),
    projectId,
    promise: requestWorkflowConfig(projectId).catch(error => {
      if (cached === entry) cached = null
      throw error
    }),
  }
  cached = entry
  return entry.promise
}

export function resetWorkflowConfigCache(): void {
  cached = null
}
